/* eslint-disable react-hooks/exhaustive-deps -- Reset booking state when case changes (caseInfo.internalId); intentionally omit setState/clearAnswers to avoid unnecessary effect runs */
import './online-booking-page.css';
import { App, Button, Skeleton } from 'antd';
import { SalesCallCalendar } from '../../appointment-booking/sales-call-calendar';
import { useEffect, useState } from 'react';
import { OpportunityComplete, CaseInfo, CaseStatus, Slot, SalesChannel, OpportunityEditContactDto } from '../../../dto/model';
import { strings } from '../../../lang';
import { CustomerLostInfo, CustomerLostModal, LostReasonSelectOption } from '../modal/customer-lost-modal';
import CustomerInfoSection from './online-booking-page-cust-info';
import { NotReachedModal } from '../modal/not-reached-modal';
import { ReachedButNotBookedBody, ReachedButNotBookedInfo } from '../modal/reached-not-booked';
import CaseCommentSection from './online-booking-page-case-comments';
import WrongPhoneNumberModal from '../modal/wrong-phone-number-modal';
import { useMsal } from '@azure/msal-react';
import { GetAccessToken } from '../../../utils/auth-utils';
import { scopes } from '../../../authConfig';
import { bookingPageService } from '../../../services/booking-page-service';
import { getLostReasonAssignmentConfig } from '../../../services/lost-reason-service';
import { useSalesChannelState } from '../../../store/header-state';
import { CalendarBackButton, LoadCalendarButton, StartBookingButton } from '../../appointment-booking/calendar-button';
import { QualifyingQuestions } from '../../appointment-booking/qualifying-questions';
import { QualifyingAnswer } from '../../../utils/lead-queue-utils';
import { useQualifyingAnswersState } from '../../../store/qualifying-answers-state';
import { useShallow } from 'zustand/shallow';

const salesForceRootUrl = process.env.REACT_APP_SALESFORCE_BASE_URL ?? '';

function legacyLostReasonSelectOptions(): LostReasonSelectOption[] {
  return strings.customerLostOptions.map((o) => ({
    value: String(o.value),
    label: String(o.label),
  }));
}

const SALES_CHANNELS_WITH_LOOSE_CUSTOMER_LABEL: SalesChannel[] = [SalesChannel.Revision, SalesChannel.LeadQueue, SalesChannel.CrmSales];

function getLegacyCustomerLostButtonLabel(salesChannel: SalesChannel): string {
  return SALES_CHANNELS_WITH_LOOSE_CUSTOMER_LABEL.includes(salesChannel) ? strings.looseCustomerButton : strings.modalTitleCustomerLost;
}

type OnlineBookingPageProps = {
  caseInfo: CaseInfo | null;
  setCaseInfo: (caseInfo: CaseInfo) => void;
  cancelledSlot: Slot | null;
  inputCaseComments: string;
  setInputCaseComments: (inputComments: string) => void;
  onCaseClosed: (caseComplete: OpportunityComplete, bookedSlot?: Slot) => Promise<void>;
  inboundCenter: boolean | undefined;
  inboundAutodialed?: boolean;
  isAutodialerFlow?: boolean;
  onInboundCallback?: (bookedSlot: Slot | undefined) => void;
  bookingButtonStyle?: string;
  handleCallButtonClick: (phoneNumber: string) => void;
};

export const OnlineBookingPage = ({
  caseInfo,
  cancelledSlot,
  inputCaseComments,
  setCaseInfo,
  setInputCaseComments,
  onCaseClosed,
  inboundCenter,
  inboundAutodialed,
  isAutodialerFlow,
  onInboundCallback,
  bookingButtonStyle,
  handleCallButtonClick,
}: OnlineBookingPageProps) => {
  const { notification } = App.useApp();
  const { instance, inProgress, accounts } = useMsal();
  const { salesChannel: salesManagerSalesChannel } = useSalesChannelState(useShallow((state) => ({ salesChannel: caseInfo?.salesChannel ?? state.salesChannel })));
  const [showWrongPhoneNumberModal, setShowWrongPhoneNumberModal] = useState(false);
  const [loadCalendar, setLoadCalendar] = useState(false);
  const [showCustomerLost, setShowCustomerLost] = useState(false);
  const [lostReasonOptions, setLostReasonOptions] = useState<LostReasonSelectOption[]>([]);
  const [lostReasonsReady, setLostReasonsReady] = useState(false);
  const [lostReasonConfigUnavailable, setLostReasonConfigUnavailable] = useState(false);
  const [showNotReachedModal, setShowNotReachedModal] = useState(false);
  const [showReachedButNotBookedBody, setShowReachedButNotBookedBody] = useState(false);
  const [oppSalesChannel, setOppSalesChannel] = useState<SalesChannel | undefined>();
  const [showQualifyingQuestions, setShowQualifyingQuestions] = useState(false);
  const [showBackButton, setShowBackButton] = useState(false);
  const [storeAnswers, clearAnswers] = useQualifyingAnswersState(useShallow((state) => [state.answers, state.clearAnswers]));

  const query = new URLSearchParams(window.location.search);
  const sourceSystemType =
    query.get('type') === 'salescall2' ? 'salescall2' : query.get('type') === 'autodialer' || isAutodialerFlow ? 'autodialer' : inboundCenter ? 'inbound' : '';

  const getQualifyingAnswersForBooking = (): QualifyingAnswer[] => {
    return (
      caseInfo?.qualifyingQuestions
        ?.filter((q) => storeAnswers[q.question.id])
        .map((q) => ({
          questionId: q.question.id,
          questionText: JSON.stringify(q.question.questionText),
          answerText: storeAnswers[q.question.id],
        })) || []
    );
  };

  const buildBaseCaseComplete = (info: CaseInfo, overrides: Partial<OpportunityComplete>): OpportunityComplete => ({
    id: info.internalId,
    opportunityId: info.opportunityId,
    globalCustomerId: info.globalCustomerId,
    caseId: info.caseId,
    signUpId: info.signupId,
    customerLostReasons: null,
    customerLostDescription: null,
    opportunityComments: inputCaseComments,
    doNotCallBefore: null,
    caseStatus: CaseStatus.Booked,
    isDataDeletionRequested: false,
    wrongContactId: info.contactId,
    ...overrides,
  });

  const onBooking = async (isSuccess: boolean, bookedSlot?: Slot, error?: string) => {
    if (!caseInfo) return;
    if (isSuccess) {
      await onCaseClosed(
        buildBaseCaseComplete(caseInfo, {
          caseStatus: CaseStatus.Booked,
          qualifyingAnswers: getQualifyingAnswersForBooking(),
        }),
        bookedSlot,
      );
      return;
    }
    if (error?.includes('case is already closed')) {
      await onCaseClosed(buildBaseCaseComplete(caseInfo, { caseStatus: CaseStatus.Booked }));
    }
  };

  const onUpdatePhoneNumber = async (phoneNumber: string) => {
    if (!caseInfo) return;
    setCaseInfo({ ...caseInfo, phone: phoneNumber });
    const token = await GetAccessToken(instance, inProgress, scopes.salesAppointmentApi);
    const editContactDto: OpportunityEditContactDto = {
      firstName: caseInfo.firstName ?? '',
      lastName: caseInfo.lastName ?? '',
      salutation: caseInfo.salutation ?? '',
      email: caseInfo.email ?? '',
      phone: phoneNumber ?? '',
      opportunityId: caseInfo.opportunityId ?? '',
      globalCustomerId: caseInfo.globalCustomerId ?? '',
      signUpId: caseInfo.signupId ?? '',
      contactId: caseInfo.contactId ?? '',
      city: caseInfo.city ?? '',
      street: caseInfo.street ?? '',
      houseNumber: caseInfo.houseNumber ?? '',
      zipCode: caseInfo.zipcode ?? '',
    };
    const updateResult = await bookingPageService.updateContact(editContactDto, salesManagerSalesChannel, token.accessToken);
    if (!updateResult.isSuccess) {
      notification.error({
        message: `Failed to update phone number. Errors: ${JSON.stringify(updateResult.errors)}`,
      });
      return;
    }
    notification.info({ message: 'Phone number updated successfully' });
  };

  const handleLoadCalendar = (channel: SalesChannel) => {
    setOppSalesChannel(channel);
    setLoadCalendar(true);
    setShowQualifyingQuestions(false);
    setShowBackButton(true);
  };

  const handleClickBack = () => {
    setLoadCalendar(false);
    setShowQualifyingQuestions(true);
    setShowBackButton(false);
  };

  useEffect(() => {
    setLoadCalendar(false);
    setInputCaseComments('');
    setShowBackButton(false);
    clearAnswers();
    if (caseInfo && !caseInfo.showQualifyingQuestions) {
      setOppSalesChannel(caseInfo.salesChannel === SalesChannel.LeadQueue ? SalesChannel.DigitalSales : (caseInfo.salesChannel ?? undefined));
    }
  }, [caseInfo?.internalId]);

  const applyLegacyLostReasonFlow = () => {
    setLostReasonOptions(legacyLostReasonSelectOptions());
    setLostReasonConfigUnavailable(true);
  };

  useEffect(() => {
    if (!showCustomerLost || !caseInfo?.salesChannel) return;
    if (caseInfo.configurableLostReasonsInBooking !== true) {
      setLostReasonOptions(legacyLostReasonSelectOptions());
      setLostReasonConfigUnavailable(false);
      setLostReasonsReady(true);
      return;
    }
    let cancelled = false;
    setLostReasonsReady(false);
    setLostReasonConfigUnavailable(false);
    void (async () => {
      try {
        const token = await GetAccessToken(instance, inProgress, scopes.salesAppointmentApi);
        const res = await getLostReasonAssignmentConfig(token.accessToken, caseInfo.salesChannel!);
        if (cancelled) return;
        if (res.isSuccess && res.data?.groups?.length) {
          const lang = strings.getLanguage();
          const opts: LostReasonSelectOption[] = [];
          for (const g of res.data?.groups ?? []) {
            for (const r of g.reasons ?? []) {
              const displayDe = r.germanLabel ?? '';
              const displayEn = r.englishLabel ?? '';
              opts.push({
                value: `${g.id}:${r.id}`,
                label: lang === 'de' ? displayDe : displayEn,
                englishLabel: r.englishLabel,
                outcomeKey: g.outcomeKey,
              });
            }
          }
          if (opts.length > 0) {
            setLostReasonOptions(opts);
            setLostReasonConfigUnavailable(false);
          } else {
            applyLegacyLostReasonFlow();
          }
        } else {
          applyLegacyLostReasonFlow();
        }
      } catch {
        if (!cancelled) applyLegacyLostReasonFlow();
      } finally {
        if (!cancelled) setLostReasonsReady(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [showCustomerLost, caseInfo?.internalId, caseInfo?.salesChannel, caseInfo?.configurableLostReasonsInBooking, instance, inProgress]);

  if (!caseInfo) {
    return <Skeleton />;
  }

  const useCloseCaseFlow = caseInfo.configurableLostReasonsInBooking === true && !lostReasonConfigUnavailable;

  const filteredCaseComments = caseInfo.caseComments
    .filter((item) => (caseInfo.salesChannel === SalesChannel.Revision ? !item.isCustomerLostReason : item.isNotBookedReason))
    .sort((a, b) => new Date(b.createdOn).getTime() - new Date(a.createdOn).getTime());

  return (
    <div>
      <CustomerInfoSection
        inboundCenter={inboundCenter}
        inboundAutodialed={inboundAutodialed}
        isAutodialerFlow={isAutodialerFlow}
        caseInfo={caseInfo}
        setCaseInfo={setCaseInfo}
        cancelledSlot={cancelledSlot}
        showWrongPhoneNumberModal={setShowWrongPhoneNumberModal}
        handleCallButtonClick={handleCallButtonClick}
      />
      <CaseCommentSection caseComments={filteredCaseComments} inputCaseComment={inputCaseComments} onInputCaseCommentChange={setInputCaseComments} shouldShowPreviousComments />

      <section className="bp-section">
        <div className={`bp-calendar__buttons${isAutodialerFlow ? ' bp-calendar__buttons--three' : ''}`}>
          {caseInfo.showQualifyingQuestions && !showQualifyingQuestions && !loadCalendar && <StartBookingButton buttonStyle={bookingButtonStyle} onClick={() => setShowQualifyingQuestions(true)} />}
          {!caseInfo.showQualifyingQuestions && !loadCalendar && <LoadCalendarButton buttonStyle={bookingButtonStyle} onClick={() => setLoadCalendar(true)} />}
          <Button onClick={() => setShowReachedButNotBookedBody(true)}>{strings.modalTitleReachedNotBooked}</Button>
          {!isAutodialerFlow && (
            <Button onClick={() => setShowNotReachedModal(true)}>
              {strings.modalTitleNotReached}
            </Button>
          )}
          <Button
            type="primary"
            danger
            onClick={() => setShowCustomerLost(true)}
          >
            {useCloseCaseFlow ? strings.completeCaseButton : getLegacyCustomerLostButtonLabel(caseInfo.salesChannel!)}
          </Button>
        </div>
      </section>

      {caseInfo.showQualifyingQuestions && showQualifyingQuestions && (
        <section className="bp-calendar">
          <QualifyingQuestions questions={caseInfo.qualifyingQuestions} loadCalendar={handleLoadCalendar} buttonStyle={bookingButtonStyle} />
        </section>
      )}

      {showBackButton && <CalendarBackButton onClick={handleClickBack} />}

      {loadCalendar && (
        <section className="bp-section bp-calendar">
          <SalesCallCalendar
            opportunityId={caseInfo.opportunityId}
            signupId={caseInfo.signupId}
            onBooking={onBooking}
            skipConfirmationAfterBooking
            sourceSystemHeader={sourceSystemType}
            salesChannel={oppSalesChannel}
          />
        </section>
      )}

      <WrongPhoneNumberModal
        onUpdatePhoneNumber={onUpdatePhoneNumber}
        phoneNumber={caseInfo.phone}
        leadPhoneNumber={caseInfo.leadPhoneNumber}
        open={showWrongPhoneNumberModal}
        onOk={async (message: string) => {
          await onCaseClosed(
            buildBaseCaseComplete(caseInfo, {
              opportunityComments: message,
              caseStatus: CaseStatus.WrongNumber,
            }),
          );
          setShowWrongPhoneNumberModal(false);
        }}
        onCallButtonClick={handleCallButtonClick}
        disableCallButton={isAutodialerFlow}
        isAutodialerFlow={isAutodialerFlow}
        showModal={setShowWrongPhoneNumberModal}
      />

      <CustomerLostModal
        salesChannel={caseInfo.salesChannel!}
        inboundCenter={inboundCenter}
        isAutodialerFlow={isAutodialerFlow}
        open={showCustomerLost}
        useConfigurableLostReasons={useCloseCaseFlow}
        lostReasonsReady={lostReasonsReady}
        lostReasonOptions={lostReasonOptions}
        onOk={async (customerLostInfo: CustomerLostInfo) => {
          try {
            await onCaseClosed(
              buildBaseCaseComplete(caseInfo, {
                customerLostReasons: customerLostInfo.reasons,
                customerLostDescription: customerLostInfo.description,
                caseStatus: CaseStatus.CustomerLost,
                shareContactData: customerLostInfo.shareContactData,
                isDataDeletionRequested: customerLostInfo.isDataDeletionRequested,
              }),
            );
            setShowCustomerLost(false);
            if (inboundCenter && !isAutodialerFlow) {
              onInboundCallback?.(undefined);
            }
          } catch (error) {
            console.error('Failed to close case as customer lost:', error);
            throw error;
          }
        }}
        onCancel={() => setShowCustomerLost(false)}
        customerDeleteData={{
          bookingAgentFullName: accounts[0]?.name ?? '',
          customerFullName: `${caseInfo.firstName} ${caseInfo.lastName}`,
          contactUrl: `${salesForceRootUrl}/lightning/r/Opportunity/${caseInfo.opportunityId}/view`,
        }}
      />

      <NotReachedModal
        open={showNotReachedModal}
        onCancel={() => setShowNotReachedModal(false)}
        onOk={async () => {
          await onCaseClosed(buildBaseCaseComplete(caseInfo, { caseStatus: CaseStatus.NotReached }));
          setShowNotReachedModal(false);
        }}
      />

      <ReachedButNotBookedBody
        open={showReachedButNotBookedBody}
        onCancel={() => setShowReachedButNotBookedBody(false)}
        inboundCenter={inboundCenter}
        isAutodialerFlow={isAutodialerFlow}
        onOk={async (reachedButNotBookedInfo: ReachedButNotBookedInfo) => {
          await onCaseClosed(
            buildBaseCaseComplete(caseInfo, {
              doNotCallBefore: reachedButNotBookedInfo.dateToCall.toDate(),
              caseStatus: CaseStatus.ReachedButNotBooked,
              notBookedReason: reachedButNotBookedInfo.notBookedReason,
            }),
          );
          setShowReachedButNotBookedBody(false);
          if (inboundCenter && !isAutodialerFlow) {
            onInboundCallback?.(undefined);
          }
        }}
      />
    </div>
  );
};
